import { useEffect, useRef } from 'react';
import UserbackWidgetLoader, { getUserback, UserbackWidget as UserbackWidgetType } from '@userback/widget';

export interface UserbackWidgetProps {
  /** User's unique ID (e.g., Firebase UID) for tracking feedback history */
  userId?: string;
  /** User's email address */
  userEmail?: string;
  /** User's display name */
  userName?: string;
  /** Custom metadata to include with feedback submissions */
  metadata?: Record<string, unknown>;
}

/**
 * UserbackWidget - React component wrapper for the Userback feedback widget.
 *
 * This component initializes the Userback SDK on mount and cleans up on unmount.
 * It renders nothing visible itself - Userback injects its own floating button and modal.
 *
 * @example
 * ```tsx
 * <UserbackWidget
 *   userId={user?.uid}
 *   userEmail={user?.email}
 *   userName={user?.displayName}
 *   metadata={{ page: location.pathname }}
 * />
 * ```
 */
export function UserbackWidget({ userId, userEmail, userName, metadata }: UserbackWidgetProps) {
  const widgetRef = useRef<UserbackWidgetType | null>(null);
  const initializingRef = useRef(false);

  useEffect(() => {
    const accessToken = process.env.REACT_APP_USERBACK_ACCESS_TOKEN;

    if (!accessToken) {
      console.warn(
        '[UserbackWidget] REACT_APP_USERBACK_ACCESS_TOKEN is not configured. ' +
          'Feedback widget will not be displayed.'
      );
      return;
    }

    // Prevent double initialization in StrictMode
    if (initializingRef.current) {
      return;
    }

    // Check if already initialized
    const existingWidget = getUserback();
    if (existingWidget) {
      widgetRef.current = existingWidget;
      // Update user info if widget already exists
      if (userId) {
        const info: Record<string, string> = {};
        if (userName) info.name = userName;
        if (userEmail) info.email = userEmail;
        existingWidget.identify(userId, info);
      }
      if (metadata) existingWidget.setData(metadata);
      return;
    }

    initializingRef.current = true;

    // Build user_data only with defined values - Userback rejects undefined
    const userInfo: Record<string, string> = {};
    if (userName) userInfo.name = userName;
    if (userEmail) userInfo.email = userEmail;

    UserbackWidgetLoader(accessToken, {
      user_data: userId
        ? {
            id: userId,
            info: userInfo,
          }
        : undefined,
      custom_data: metadata,
    })
      .then((widget) => {
        widgetRef.current = widget;
      })
      .catch((error) => {
        console.error('[UserbackWidget] Failed to initialize:', error);
      })
      .finally(() => {
        initializingRef.current = false;
      });

    return () => {
      // Clean up on unmount
      if (widgetRef.current) {
        widgetRef.current.destroy();
        widgetRef.current = null;
      }
    };
  }, []);

  // Update user info when props change (after initial load)
  useEffect(() => {
    const widget = widgetRef.current || getUserback();
    if (widget) {
      if (userId) {
        const info: Record<string, string> = {};
        if (userName) info.name = userName;
        if (userEmail) info.email = userEmail;
        widget.identify(userId, info);
      }
      if (metadata) widget.setData(metadata);
    }
  }, [userId, userEmail, userName, metadata]);

  // This component doesn't render anything - Userback injects its own UI
  return null;
}

/**
 * Opens the Userback feedback widget programmatically.
 * Useful for triggering the widget from custom buttons or menu items.
 *
 * @example
 * ```tsx
 * <button onClick={() => openUserbackWidget()}>Give Feedback</button>
 * ```
 */
export function openUserbackWidget() {
  const widget = getUserback();
  if (widget) {
    widget.open();
  } else {
    console.warn('[UserbackWidget] Widget not initialized. Cannot open.');
  }
}
